import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Send } from "lucide-react";
import { useId, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { post } from "@/lib/api";
import { centsEur, dialogBlockers, mailChanges, pdfName, sendSummary } from "@/lib/documents";
import { date as deDate } from "@/lib/format";
import type { Approval, Document, DocumentDetail, DocumentPreview, MailDraft } from "@/lib/types";
import { FINALIZE_DISABLED } from "../../../invoicing/constants.ts";
import { DOC_TYPE_LABELS } from "../../../invoicing/types.ts";
import { ApprovalFooter, ApprovalNoticeLine, askedAt } from "./ApprovalBanner";
import { useApprovalDecision, useRefreshDocument } from "./useDocument";

export type SendKind = "document" | "reminder";
type ActionResult = { status: "done"; result: Document } | { status: "pending"; approval: Approval };

/** What a send carries over to the detail page's „Ist raus“ banner. */
export interface SentState { copyTo: string | null }

const OVERRIDE = "Trotzdem ohne Umsatzsteuer festschreiben (wird im Verlauf festgehalten)";

interface Fields { to: string; subject: string; text: string; copyToMe: boolean; override: boolean }

function initialFields(mail: MailDraft, approval: Approval | undefined): Fields {
  const a = approval?.args ?? {};
  const str = (k: string, fallback: string) => (typeof a[k] === "string" ? (a[k] as string) : fallback);
  return {
    to: str("to", mail.to ?? ""),
    subject: str("subject", mail.subject),
    text: str("text", mail.text),
    copyToMe: a.copyToMe !== false,
    override: a.overrideThreshold === true,
  };
}

/** „2 Seiten · vom Agent Seite für Seite geprüft“, from the preview the editor already rendered. */
function usePages(doc: Document) {
  const preview = useQuery({
    queryKey: ["preview", doc.id, doc.updatedAt],
    queryFn: () => post<DocumentPreview>(`/api/documents/${doc.id}/preview`),
    staleTime: Infinity,
  });
  return preview.data?.pageCount ?? null;
}

function FieldRow({ label, htmlFor, children, changed }: { label: string; htmlFor: string; children: React.ReactNode; changed?: boolean }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:items-center sm:gap-3">
      <label htmlFor={htmlFor} className="text-[0.8125rem] text-muted-foreground">
        {label}
        {changed && <span className="ml-1.5 rounded-sm bg-hl px-1 text-[0.6875rem] text-foreground sm:ml-0 sm:block sm:w-fit">geändert</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * *Senden*: the mail as it goes out, editable; „Senden“ finalizes a draft and mails it, „Ohne E-Mail festschreiben“
 * only finalizes. With `approval` it is Florian's check of an agent's request: the stored mail fields, „Ablehnen“ and
 * „Freigeben & senden“. `kind: "reminder"` is the Zahlungserinnerung of an open invoice.
 */
export function SendDialog({
  detail, kind = "document", approval, onClose,
}: {
  detail: DocumentDetail;
  kind?: SendKind;
  approval?: Approval;
  onClose: () => void;
}) {
  const doc = detail.document;
  const navigate = useNavigate();
  const refresh = useRefreshDocument(doc.id);
  const ids = useId();
  const mail = (kind === "reminder" ? detail.reminderMail : null) ?? detail.mail;
  const [f, setF] = useState(() => initialFields(mail, approval));
  const set = (patch: Partial<Fields>) => setF((v) => ({ ...v, ...patch }));
  const pages = usePages(doc);
  const decision = useApprovalDecision({ id: approval?.id ?? 0, documentId: doc.id });

  const finalizeOnly = approval?.action === "finalize";
  const draft = doc.finalizedAt === null;
  const askedNumber = typeof approval?.args.number === "string" ? approval.args.number : null;
  const number = askedNumber ?? doc.number ?? detail.nextNumber;
  const word = DOC_TYPE_LABELS[doc.type];
  const title = kind === "reminder"
    ? `Zahlungserinnerung zu ${number} senden`
    : finalizeOnly ? `${number ?? word} festschreiben` : `${number ?? word} senden`;
  const summary = kind === "reminder"
    ? `Offen sind ${centsEur(doc.totalCents - doc.paidCents)} von ${centsEur(doc.totalCents)}${doc.dueDate ? `, fällig seit ${deDate(doc.dueDate)}` : ""}.`
    : sendSummary(doc, number, finalizeOnly ? "finalize" : "send");

  const kuBlocked = draft && detail.validation.errors.some((e) => e.code === "ku_threshold_exceeded");
  const disabledByFlag = !detail.finalizeEnabled;
  // A draft's request names the number it would get; once another document took it, approving would be refused.
  const numberMoved = approval && draft && askedNumber !== null && askedNumber !== detail.nextNumber
    ? `Die Nummer ${askedNumber} ist inzwischen vergeben – das Dokument bekäme ${detail.nextNumber ?? "keine Nummer"}. Der Agent muss neu anfragen.`
    : null;
  const changed = approval && !finalizeOnly ? mailChanges(approval.args, mail) : [];
  // Errors the dialog cannot settle (the address field and the override checkbox it can). An agent's request may be
  // older than an edit that broke the draft: approving stays impossible, and the request stays open.
  const blockers = draft && kind === "document" ? dialogBlockers(detail.validation) : [];
  const blocked = disabledByFlag || (kuBlocked && !f.override) || numberMoved !== null || blockers.length > 0;

  const sent = (result: Document) => {
    onClose();
    if (kind === "reminder") {
      toast.success("Zahlungserinnerung versendet");
      return;
    }
    const state: SentState = { copyTo: f.copyToMe ? mail.copyTo : null };
    navigate(`/rechnungen/${result.id}?gesendet=1`, { state });
  };
  const finalized = (result: Document) => {
    onClose();
    toast.success(`Festgeschrieben als ${result.number}`);
    navigate(`/rechnungen/${result.id}`);
  };

  const mailBody = () => ({ to: f.to.trim(), subject: f.subject, text: f.text, copyToMe: f.copyToMe });
  const direct = useMutation({
    mutationFn: async (what: "send" | "finalize") => {
      const path = what === "finalize" ? "finalize" : kind === "reminder" ? "reminder" : "send";
      const body = what === "finalize"
        ? { overrideThreshold: f.override || undefined }
        : { ...mailBody(), overrideThreshold: kuBlocked ? f.override : undefined, number: kind === "document" ? (number ?? undefined) : undefined };
      return { what, r: await post<ActionResult>(`/api/documents/${doc.id}/${path}`, body) };
    },
    onSuccess: ({ what, r }) => {
      if (r.status === "pending") {
        onClose();
        toast.info("Wartet auf Freigabe");
        return;
      }
      if (what === "finalize") finalized(r.result);
      else sent(r.result);
    },
    onSettled: refresh,
  });

  const approveNow = () => {
    const overrides = finalizeOnly ? {} : mailBody();
    decision.approve.mutate(
      { ...overrides, ...(kuBlocked ? { overrideThreshold: f.override } : {}) },
      {
        onSuccess: () => {
          if (finalizeOnly) {
            onClose();
            toast.success("Festgeschrieben");
            navigate(`/rechnungen/${doc.id}`);
          } else sent(doc);
        },
      },
    );
  };

  const error = direct.error?.message ?? decision.approve.error?.message ?? null;
  const busy = direct.isPending || decision.approve.isPending;
  const noRecipient = !finalizeOnly && f.to.trim() === "";
  const open = !approval || approval.status === "pending";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-[40rem]"
        // Focus the dialog, not the first field: a keystroke must not replace the prefilled recipient.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <DialogHeader className="gap-1.5 px-6 pt-5.5 pr-12 pb-4.5 text-left">
          {approval && (
            <p className="text-[0.8125rem] text-muted-foreground">{approval.title} · {askedAt(approval.requestedAt)}</p>
          )}
          <DialogTitle className="text-lg tracking-[-0.01em]">{title}</DialogTitle>
          <DialogDescription className="text-pretty">{summary}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2.5 px-6 pb-5">
          {approval && <ApprovalNoticeLine approval={approval} number={number} />}
          {changed.length > 0 && (
            <p className="text-[0.8125rem] text-muted-foreground">
              Der Agent hat {changed.join(", ")} gegenüber dem Standard geändert.
            </p>
          )}
          {!finalizeOnly && (
            <>
              <FieldRow label="An" htmlFor={`${ids}-to`} changed={changed.includes("An")}>
                <Input
                  id={`${ids}-to`}
                  type="email"
                  autoComplete="off"
                  value={f.to}
                  readOnly={!open}
                  onChange={(e) => set({ to: e.target.value })}
                  placeholder="E-Mail-Adresse"
                  aria-invalid={noRecipient || undefined}
                  className="pointer-coarse:h-11"
                />
              </FieldRow>
              <FieldRow label="Betreff" htmlFor={`${ids}-subject`} changed={changed.includes("Betreff")}>
                <Input id={`${ids}-subject`} value={f.subject} readOnly={!open} onChange={(e) => set({ subject: e.target.value })} className="pointer-coarse:h-11" />
              </FieldRow>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:gap-3">
                <label htmlFor={`${ids}-text`} className="text-[0.8125rem] text-muted-foreground sm:pt-2.5">
                  Nachricht
                  {changed.includes("Nachricht") && <span className="ml-1.5 rounded-sm bg-hl px-1 text-[0.6875rem] text-foreground sm:ml-0 sm:block sm:w-fit">geändert</span>}
                </label>
                <Textarea id={`${ids}-text`} rows={6} value={f.text} readOnly={!open} onChange={(e) => set({ text: e.target.value })} className="leading-normal" />
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[4.5rem_minmax(0,1fr)] sm:items-center sm:gap-3">
                <span className="text-[0.8125rem] text-muted-foreground">Anhang</span>
                <a
                  href={`/api/documents/${doc.id}/pdf`}
                  target="_blank"
                  rel="noopener"
                  className="flex min-h-10 min-w-0 items-center gap-2.5 rounded-md border px-3 py-1.5 outline-none transition-colors duration-150 hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none pointer-coarse:min-h-11"
                >
                  <FileText aria-hidden className="size-4.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm">{pdfName(doc, number)}</span>
                  {pages !== null && (
                    <span className="truncate text-xs text-muted-foreground">
                      {pages === 1 ? "1 Seite" : `${pages} Seiten`}{detail.previewSeenByAgent ? " · vom Agent Seite für Seite geprüft" : ""}
                    </span>
                  )}
                </a>
              </div>
              {mail.copyTo && (
                <label className="flex min-h-6 items-center gap-2 text-sm sm:pl-[5.25rem] pointer-coarse:min-h-11">
                  <Checkbox checked={f.copyToMe} disabled={!open} onCheckedChange={(v) => set({ copyToMe: v === true })} />
                  Kopie an {mail.copyTo}
                </label>
              )}
            </>
          )}
          {kuBlocked && (
            <div className="flex flex-col gap-1.5 rounded-md border border-warning/60 bg-warning/10 p-3">
              <p className="text-[0.8125rem]">{detail.validation.errors.find((e) => e.code === "ku_threshold_exceeded")?.message}</p>
              <label className="flex items-start gap-2 text-sm pointer-coarse:min-h-11 pointer-coarse:items-center">
                <Checkbox className="mt-0.5 pointer-coarse:mt-0" checked={f.override} disabled={!open} onCheckedChange={(v) => set({ override: v === true })} />
                {OVERRIDE}
              </label>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background px-6 py-3.5">
          {disabledByFlag && <p role="status" className="text-[0.8125rem] text-pretty text-muted-foreground">{FINALIZE_DISABLED}</p>}
          {numberMoved && <p role="alert" className="text-[0.8125rem] text-destructive">{numberMoved}</p>}
          {blockers.length > 0 && (
            <div role="alert" className="text-[0.8125rem] text-destructive">
              <p>Erst festschreibbar, wenn das behoben ist:</p>
              <ul className="list-disc pl-5">
                {blockers.map((e) => <li key={e.code + e.message}>{e.message}</li>)}
              </ul>
            </div>
          )}
          {error && <p role="alert" className="text-[0.8125rem] text-destructive">{error}</p>}
          {approval ? (
            <ApprovalFooter
              approval={approval}
              primary={finalizeOnly ? "Freigeben & festschreiben" : "Freigeben & senden"}
              disabled={blocked || busy || noRecipient}
              onApprove={approveNow}
              onClose={onClose}
            />
          ) : (
            <DialogFooter className="flex-row flex-wrap items-center gap-2 sm:justify-start">
              {draft && kind === "document" && (
                <Button
                  variant="ghost"
                  className="px-2 text-muted-foreground max-sm:order-last max-sm:basis-full pointer-coarse:h-11"
                  disabled={blocked || busy}
                  onClick={() => direct.mutate("finalize")}
                >
                  Ohne E-Mail festschreiben
                </Button>
              )}
              <Button variant="outline" className="ml-auto pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
              <Button className="pointer-coarse:h-11" disabled={blocked || busy || noRecipient} onClick={() => direct.mutate("send")}>
                <Send aria-hidden />
                Senden
              </Button>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
